import { useState, useEffect } from 'react';
import { 
  Building2,
  Truck,
  Users,
  UserCheck,
  User,
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  Eye,
  Phone,
  Mail,
  Calendar,
  MapPin,
  Package,
  AlertCircle,
  CheckCircle,
  Clock,
  Download,
  Upload,
  RefreshCw
} from 'lucide-react';

const Transportistas = () => {
  const [transportistas, setTransportistas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTransportista, setSelectedTransportista] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('view'); // 'view', 'edit', 'create'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Datos de ejemplo basados en el curl proporcionado
  const sampleTransportistas = [
    {
      empresaId: "550e8400-e29b-41d4-a716-446655440000",
      empresaNombre: "Transportista ABC",
      tipoEmpresa: "transportista",
      ruc: "20123456789",
      estado: "activo",
      fechaRegistro: "2024-01-15",
      contacto: {
        telefono: "01-234-5678",
        email: "contacto@transportistaabc.com",
        direccion: "Av. Principal 123, Lima"
      },
      camiones: [
        {
          placa: "ABC-001",
          marca: "Volvo",
          modelo: "FH16",
          ano: 2022,
          capacidadToneladas: 20.50,
          estado: "activo"
        },
        {
          placa: "ABC-002",
          marca: "Scania",
          modelo: "R450",
          ano: 2023,
          capacidadToneladas: 18.00,
          estado: "activo"
        }
      ],
      choferes: [
        {
          dni: "12345678",
          nombre: "Juan",
          apellidos: "Perez",
          telefono: "987654321",
          email: "juan.perez@email.com",
          licenciaNumero: "LIC001",
          licenciaCategoria: "A",
          licenciaVencimiento: "2026-12-31",
          estado: "activo"
        },
        {
          dni: "87654321",
          nombre: "Maria",
          apellidos: "Gomez",
          telefono: "123456789",
          email: "maria.gomez@email.com",
          licenciaNumero: "LIC002",
          licenciaCategoria: "B",
          licenciaVencimiento: "2025-06-30",
          estado: "activo"
        }
      ],
      operadoresLogisticos: [
        {
          operadorId: "123e4567-e89b-12d3-a456-426614174001",
          operadorNombre: "Operador Logistico 1",
          clientes: [
            {
              clienteId: "789e0123-e89b-12d3-a456-426614174003",
              clienteNombre: "Exportador Cliente 1",
              clienteRuc: "20123456792"
            },
            {
              clienteId: "789e0123-e89b-12d3-a456-426614174004",
              clienteNombre: "Exportador Cliente 2",
              clienteRuc: "20123456793"
            }
          ]
        }
      ]
    },
    {
      empresaId: "550e8400-e29b-41d4-a716-446655440001",
      empresaNombre: "Logística del Norte SAC",
      tipoEmpresa: "transportista",
      ruc: "20987654321",
      estado: "activo",
      fechaRegistro: "2024-02-20",
      contacto: {
        telefono: "01-567-8901",
        email: "contacto@logisticanorte.com",
        direccion: "Jr. Comercio 456, Trujillo"
      },
      camiones: [
        {
          placa: "LDN-003",
          marca: "Mercedes-Benz",
          modelo: "Actros",
          ano: 2021,
          capacidadToneladas: 25.00,
          estado: "activo"
        }
      ],
      choferes: [
        {
          dni: "45678912",
          nombre: "Carlos",
          apellidos: "Rodriguez",
          telefono: "945123456",
          email: "carlos.rodriguez@email.com",
          licenciaNumero: "LIC003",
          licenciaCategoria: "A",
          licenciaVencimiento: "2025-12-15",
          estado: "activo"
        }
      ],
      operadoresLogisticos: []
    }
  ];

  // Función para cargar datos del transportista desde API
  const loadTransportistaData = async (empresaId) => {
    setLoading(true);
    try {
      const response = await fetch(`https://8c491507d9cd.ngrok-free.app/api/transportista/${empresaId}?tipoEmpresa=transportista`);
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        throw new Error('Error al cargar datos del transportista');
      }
    } catch (error) {
      console.error('Error:', error);
      // En caso de error, usar datos de ejemplo
      return sampleTransportistas.find(t => t.empresaId === empresaId);
    } finally {
      setLoading(false);
    }
  };

  // Cargar lista de transportistas al inicializar
  useEffect(() => {
    setTransportistas(sampleTransportistas);
  }, []);

  // Funciones de filtrado y búsqueda
  const filteredTransportistas = transportistas.filter(transportista => {
    const matchesSearch = transportista.empresaNombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transportista.ruc.includes(searchTerm);
    const matchesFilter = filterStatus === 'all' || transportista.estado === filterStatus;
    return matchesSearch && matchesFilter;
  });

  // Paginación
  const totalPages = Math.ceil(filteredTransportistas.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentTransportistas = filteredTransportistas.slice(startIndex, startIndex + itemsPerPage);

  // Handlers para modales
  const handleViewTransportista = async (transportista) => {
    const fullData = await loadTransportistaData(transportista.empresaId);
    setSelectedTransportista(fullData);
    setModalType('view');
    setShowModal(true);
  };

  const handleEditTransportista = (transportista) => {
    setSelectedTransportista(transportista);
    setModalType('edit');
    setShowModal(true);
  };

  const handleCreateTransportista = () => {
    setSelectedTransportista(null);
    setModalType('create');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTransportista(null);
  };

  // Función para obtener el color del estado
  const getStatusColor = (estado) => {
    switch (estado) {
      case 'activo':
        return 'bg-green-100 text-green-800';
      case 'inactivo':
        return 'bg-red-100 text-red-800';
      case 'suspendido':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Modal de visualización/edición
  const renderModal = () => {
    if (!showModal) return null;

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center">
        {/* Overlay */}
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
          onClick={closeModal}
        />
        
        {/* Modal */}
        <div className="relative bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-white/20 max-w-6xl max-h-[90vh] mx-4 overflow-hidden">
          {/* Header del Modal */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-8 py-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Building2 className="mr-3 h-8 w-8" />
                <div>
                  <h2 className="text-2xl font-bold">
                    {modalType === 'create' ? 'Nuevo Transportista' : 
                     modalType === 'edit' ? 'Editar Transportista' : 
                     'Detalles del Transportista'}
                  </h2>
                  {selectedTransportista && (
                    <p className="text-indigo-100">{selectedTransportista.empresaNombre}</p>
                  )}
                </div>
              </div>
              <button
                onClick={closeModal}
                className="p-2 rounded-lg hover:bg-white/20 transition-colors duration-200"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Contenido del Modal */}
          <div className="p-8 overflow-y-auto max-h-[calc(90vh-120px)]">
            {selectedTransportista && (
              <div className="space-y-8">
                {/* Información General */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Datos de la Empresa */}
                  <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-xl border border-indigo-200">
                    <h3 className="text-xl font-semibold text-indigo-900 mb-4 flex items-center">
                      <Building2 className="mr-2 h-5 w-5" />
                      Información de la Empresa
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Nombre de la Empresa</label>
                        <p className="text-lg font-semibold text-indigo-900">{selectedTransportista.empresaNombre}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-600">RUC</label>
                          <p className="font-semibold text-gray-900">{selectedTransportista.ruc}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Estado</label>
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedTransportista.estado)}`}>
                            {selectedTransportista.estado}
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Tipo de Empresa</label>
                        <p className="font-semibold text-gray-900 capitalize">{selectedTransportista.tipoEmpresa}</p>
                      </div>
                    </div>
                  </div>

                  {/* Información de Contacto */}
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-200">
                    <h3 className="text-xl font-semibold text-blue-900 mb-4 flex items-center">
                      <Phone className="mr-2 h-5 w-5" />
                      Información de Contacto
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 text-blue-500 mr-2" />
                        <span className="text-gray-600 text-sm mr-2">Teléfono:</span>
                        <span className="font-semibold">{selectedTransportista.contacto?.telefono || 'No especificado'}</span>
                      </div>
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 text-blue-500 mr-2" />
                        <span className="text-gray-600 text-sm mr-2">Email:</span>
                        <span className="font-semibold">{selectedTransportista.contacto?.email || 'No especificado'}</span>
                      </div>
                      <div className="flex items-start">
                        <MapPin className="h-4 w-4 text-blue-500 mr-2 mt-1" />
                        <div>
                          <span className="text-gray-600 text-sm">Dirección:</span>
                          <p className="font-semibold">{selectedTransportista.contacto?.direccion || 'No especificada'}</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-blue-500 mr-2" />
                        <span className="text-gray-600 text-sm mr-2">Fecha de Registro:</span>
                        <span className="font-semibold">{selectedTransportista.fechaRegistro || 'No especificada'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Flota de Camiones */}
                <div className="bg-white p-6 rounded-xl shadow-sm border">
                  <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                    <Truck className="mr-2 h-6 w-6 text-blue-500" />
                    Flota de Camiones ({selectedTransportista.camiones?.length || 0})
                  </h3>
                  {selectedTransportista.camiones && selectedTransportista.camiones.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {selectedTransportista.camiones.map((camion, index) => (
                        <div key={index} className="bg-gradient-to-br from-gray-50 to-blue-50 p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h4 className="font-bold text-xl text-gray-900">{camion.placa}</h4>
                              <p className="text-gray-600">{camion.marca} {camion.modelo}</p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(camion.estado)}`}>
                              {camion.estado}
                            </span>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Año:</span>
                              <span className="font-medium">{camion.ano || 'No especificado'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Capacidad:</span>
                              <span className="font-medium text-blue-600">{camion.capacidadToneladas} ton</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Truck className="mx-auto h-12 w-12 mb-4 opacity-50" />
                      <p>No hay camiones registrados</p>
                    </div>
                  )}
                </div>

                {/* Lista de Choferes */}
                <div className="bg-white p-6 rounded-xl shadow-sm border">
                  <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                    <Users className="mr-2 h-6 w-6 text-green-500" />
                    Choferes ({selectedTransportista.choferes?.length || 0})
                  </h3>
                  {selectedTransportista.choferes && selectedTransportista.choferes.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {selectedTransportista.choferes.map((chofer, index) => (
                        <div key={index} className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-lg border border-green-200">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h4 className="font-semibold text-lg text-gray-900">
                                {chofer.nombre} {chofer.apellidos}
                              </h4>
                              <p className="text-gray-600 text-sm">DNI: {chofer.dni}</p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(chofer.estado)}`}>
                              {chofer.estado}
                            </span>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center">
                              <Phone className="h-3 w-3 text-green-500 mr-2" />
                              <span className="text-gray-600 mr-2">Teléfono:</span>
                              <span className="font-medium">{chofer.telefono}</span>
                            </div>
                            <div className="flex items-center">
                              <Mail className="h-3 w-3 text-green-500 mr-2" />
                              <span className="text-gray-600 mr-2">Email:</span>
                              <span className="font-medium text-xs">{chofer.email}</span>
                            </div>
                            <div className="flex items-center">
                              <Package className="h-3 w-3 text-green-500 mr-2" />
                              <span className="text-gray-600 mr-2">Licencia:</span>
                              <span className="font-medium">{chofer.licenciaNumero} (Cat. {chofer.licenciaCategoria})</span>
                            </div>
                            <div className="flex items-center">
                              <Calendar className="h-3 w-3 text-green-500 mr-2" />
                              <span className="text-gray-600 mr-2">Vencimiento:</span>
                              <span className="font-medium">{chofer.licenciaVencimiento}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="mx-auto h-12 w-12 mb-4 opacity-50" />
                      <p>No hay choferes registrados</p>
                    </div>
                  )}
                </div>

                {/* Operadores Logísticos */}
                {selectedTransportista.operadoresLogisticos && selectedTransportista.operadoresLogisticos.length > 0 && (
                  <div className="bg-white p-6 rounded-xl shadow-sm border">
                    <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                      <UserCheck className="mr-2 h-6 w-6 text-purple-500" />
                      Operadores Logísticos Asociados
                    </h3>
                    <div className="space-y-6">
                      {selectedTransportista.operadoresLogisticos.map((operador, index) => (
                        <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-lg border border-purple-200">
                          <h4 className="font-semibold text-lg text-purple-900 mb-4">{operador.operadorNombre}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {operador.clientes.map((cliente, clienteIndex) => (
                              <div key={clienteIndex} className="bg-white p-4 rounded-lg border border-purple-200 shadow-sm">
                                <div className="flex items-start">
                                  <User className="h-4 w-4 text-purple-500 mr-2 mt-1" />
                                  <div>
                                    <h5 className="font-medium text-gray-900 text-sm">{cliente.clienteNombre}</h5>
                                    <p className="text-gray-600 text-xs">RUC: {cliente.clienteRuc}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer del Modal */}
          <div className="bg-gray-50 px-8 py-4 border-t border-gray-200 flex justify-end space-x-3">
            <button
              onClick={closeModal}
              className="px-6 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors duration-200"
            >
              Cerrar
            </button>
            {modalType === 'view' && (
              <button
                onClick={() => handleEditTransportista(selectedTransportista)}
                className="px-6 py-2 text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 rounded-lg font-medium transition-all duration-200"
              >
                Editar
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white/80 backdrop-blur-md p-8 rounded-2xl shadow-xl border border-white/20">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Gestión de Transportistas
          </h2>
          <p className="text-gray-600 mt-2">Administra empresas transportistas, camiones y choferes asociados</p>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={handleCreateTransportista}
            className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-indigo-600 hover:to-indigo-700 flex items-center shadow-lg transform hover:scale-105 transition-all duration-200"
          >
            <Plus className="mr-2 h-5 w-5" />
            Nuevo Transportista
          </button>
          <button className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl hover:from-green-600 hover:to-green-700 flex items-center shadow-lg transform hover:scale-105 transition-all duration-200">
            <Upload className="mr-2 h-5 w-5" />
            Importar
          </button>
        </div>
      </div>

      {/* Filtros y Búsqueda */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-6 rounded-xl border border-gray-200 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            {/* Búsqueda */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nombre o RUC..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white transition-all duration-200 w-full sm:w-80"
              />
            </div>
            
            {/* Filtro por estado */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="pl-10 pr-8 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white transition-all duration-200 appearance-none cursor-pointer"
              >
                <option value="all">Todos los estados</option>
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
                <option value="suspendido">Suspendido</option>
              </select>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">
              {filteredTransportistas.length} transportista(s) encontrado(s)
            </span>
            <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors duration-200">
              <RefreshCw className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Tabla de Transportistas */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold">Empresa</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">RUC</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Estado</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Camiones</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Choferes</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Fecha Registro</th>
                <th className="px-6 py-4 text-center text-sm font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentTransportistas.map((transportista) => (
                <tr key={transportista.empresaId} className="hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="bg-gradient-to-br from-indigo-100 to-purple-100 p-2 rounded-lg mr-3">
                        <Building2 className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{transportista.empresaNombre}</div>
                        <div className="text-sm text-gray-500">{transportista.tipoEmpresa}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 font-mono">{transportista.ruc}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(transportista.estado)}`}>
                      {transportista.estado}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Truck className="h-4 w-4 text-blue-500 mr-1" />
                      <span className="text-sm font-medium">{transportista.camiones?.length || 0}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Users className="h-4 w-4 text-green-500 mr-1" />
                      <span className="text-sm font-medium">{transportista.choferes?.length || 0}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{transportista.fechaRegistro}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center space-x-2">
                      <button
                        onClick={() => handleViewTransportista(transportista)}
                        className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                        title="Ver detalles"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditTransportista(transportista)}
                        className="p-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors duration-200"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Mostrando {startIndex + 1} a {Math.min(startIndex + itemsPerPage, filteredTransportistas.length)} de {filteredTransportistas.length} transportistas
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Anterior
                </button>
                
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`px-3 py-2 text-sm rounded-lg transition-colors duration-200 ${
                      currentPage === index + 1
                        ? 'bg-indigo-500 text-white'
                        : 'text-gray-600 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Estadísticas rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Total Transportistas</p>
              <p className="text-3xl font-bold text-blue-900">{transportistas.length}</p>
            </div>
            <Building2 className="h-12 w-12 text-blue-500" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Transportistas Activos</p>
              <p className="text-3xl font-bold text-green-900">
                {transportistas.filter(t => t.estado === 'activo').length}
              </p>
            </div>
            <CheckCircle className="h-12 w-12 text-green-500" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">Total Camiones</p>
              <p className="text-3xl font-bold text-purple-900">
                {transportistas.reduce((total, t) => total + (t.camiones?.length || 0), 0)}
              </p>
            </div>
            <Truck className="h-12 w-12 text-purple-500" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl border border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-orange-600">Total Choferes</p>
              <p className="text-3xl font-bold text-orange-900">
                {transportistas.reduce((total, t) => total + (t.choferes?.length || 0), 0)}
              </p>
            </div>
            <Users className="h-12 w-12 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Estado de carga */}
      {loading && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="bg-white/95 backdrop-blur-md rounded-2xl p-8 shadow-2xl border border-white/20">
            <div className="flex items-center space-x-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
              <span className="text-lg font-medium text-gray-900">Cargando datos...</span>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {renderModal()}
    </div>
  );
};

export default Transportistas;